<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import MessageFormatter from 'shared/helpers/MessageFormatter.js';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const { variant, orientation, inReplyTo, shouldGroupWithNext } =
  useMessageContext();
const { t } = useI18n();

// Bloom: neutral, Front-style message styling. Customer/agent direction
// is conveyed by avatar placement (private notes also get an "Internal"
// pill in their header). All other messages share a uniform background
// so the eye isn't fighting a kindergarten palette.
//
// Emails are full-width white cards. Private notes are full-width slate
// cards (a hair darker than the surface, distinct from white emails).
// The cards' borders + small shadow are the only visual separation
// between messages — no row dividers.
const variantBaseMap = {
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-alpha-1 text-n-slate-12',
  // Bloom: private notes are full-width Front-style cards. Subtle
  // gray tint differentiates them from white email cards without
  // adding color noise. The "Internal" pill in the header carries
  // the actual "this is internal" affordance.
  [MESSAGE_VARIANTS.PRIVATE]:
    'w-full bg-n-slate-2 border border-n-slate-3 rounded-lg overflow-hidden shadow-sm text-n-slate-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-alpha-1 text-n-slate-12',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-transparent text-n-slate-11 text-xs',
  [MESSAGE_VARIANTS.BOT]: 'bg-n-alpha-1 text-n-slate-12',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-alpha-1 text-n-slate-12',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-3 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-transparent border border-dashed border-n-slate-6 text-n-slate-11',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const messageClass = computed(() => {
  const classToApply = [variantBaseMap[variant.value]];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(() => {
  if (props.hideMeta) return false;
  if (variant.value === MESSAGE_VARIANTS.ACTIVITY) return false;
  // Bloom: EmailBubble + private-note TextBubble now render their own
  // header row (avatar + sender + timestamp) at the top, matching Front.
  // The bottom timestamp footer is redundant and adds visual noise.
  if (variant.value === MESSAGE_VARIANTS.EMAIL) return false;
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) return false;
  if (shouldGroupWithNext.value) return false;
  return true;
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content, attachments } = inReplyTo.value;

  if (content) return new MessageFormatter(content).formattedMessage;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;

    return t(`CHAT_LIST.ATTACHMENTS.${fileType}.CONTENT`);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        // Bloom: scale message width with viewport. Emails and private
        // notes are full-width so the thread reads top-to-bottom like
        // Front. Other message types (chat-style channels) cap to keep
        // bubbles from sprawling on wide screens.
        'max-w-lg md:max-w-xl xl:max-w-3xl':
          variant !== MESSAGE_VARIANTS.EMAIL &&
          variant !== MESSAGE_VARIANTS.PRIVATE,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      @click="scrollToMessage"
    >
      <div
        v-dompurify-html="replyToPreview"
        class="prose prose-bubble line-clamp-2"
      />
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        variant === MESSAGE_VARIANTS.PRIVATE
          ? 'text-n-amber-12/50'
          : 'text-n-slate-11',
      ]"
      class="mt-2"
    />
  </div>
</template>
